"use client";
import { useState, useRef, useEffect } from "react";
import Image from "next/image";

interface SelectOption {
    value: string;
    label: string;
    flagUrl?: string;
    fullLabel?: string;
}

interface CustomSelectProps {
    value: string | string[];
    onChange: (value: string | string[]) => void;
    options: SelectOption[];
    placeholder: string;
    disabled?: boolean;
    error?: string;
    className?: string;
    isMulti?: boolean;
    maxItems?: number;
    showFlags?: boolean;
    compact?: boolean;
}

const CustomSelect = ({
    value,
    onChange,
    options,
    placeholder,
    disabled = false,
    error,
    className = "",
    isMulti = false,
    maxItems = 999,
    showFlags = false,
    compact = false
}: CustomSelectProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const [dropdownWidth, setDropdownWidth] = useState<number | null>(null);
    const selectRef = useRef<HTMLDivElement>(null);
    const optionsRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const optionRefs = useRef<(HTMLDivElement | null)[]>([]);

    // Calculate dropdown width for compact mode
    useEffect(() => {
        if (compact && isOpen && selectRef.current) {
            const container = selectRef.current.closest('.phone-input-container');
            if (container) {
                setDropdownWidth(container.clientWidth);
            }
        }
    }, [compact, isOpen]);

    // Helper to check if an option is selected
    const isOptionSelected = (optionValue: string) => {
        if (isMulti) {
            return Array.isArray(value) && value.includes(optionValue);
        }
        return value === optionValue;
    };

    const filteredOptions = options.filter(option =>
        option.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (option.fullLabel && option.fullLabel.toLowerCase().includes(searchTerm.toLowerCase())) ||
        option.value.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const getSelectedLabel = () => {
        if (isMulti) {
            return placeholder; // Return placeholder if empty, otherwise chips are shown
        }
        const option = options.find(opt => opt.value === value);
        return option ? option.label : placeholder;
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        const handleWheel = (event: WheelEvent) => {
            if (isOpen && optionsRef.current && optionsRef.current.contains(event.target as Node)) {
                const element = optionsRef.current;
                const { scrollTop, scrollHeight, clientHeight } = element;
                const maxScroll = scrollHeight - clientHeight;
                const isAtTop = scrollTop <= 0;
                const isAtBottom = scrollTop >= maxScroll;
                const isScrollingDown = event.deltaY > 0;
                const isScrollingUp = event.deltaY < 0;

                event.preventDefault();
                event.stopPropagation();

                if ((isScrollingDown && !isAtBottom) || (isScrollingUp && !isAtTop)) {
                    element.scrollTop += event.deltaY;
                }
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('wheel', handleWheel, { passive: false });

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('wheel', handleWheel);
        };
    }, [isOpen]);

    useEffect(() => {
        if (isOpen && highlightedIndex >= 0) {
            optionRefs.current[highlightedIndex]?.scrollIntoView({
                block: 'nearest',
                behavior: 'smooth'
            });
        }
    }, [highlightedIndex, isOpen]);

    useEffect(() => {
        if (isOpen) {
            searchInputRef.current?.focus();
        } else {
            setSearchTerm("");
        }
    }, [isOpen]);

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (disabled) return;

        switch (e.key) {
            case 'Enter':
            case ' ':
                e.preventDefault();
                if (isOpen && highlightedIndex >= 0) {
                    handleOptionClick(options[highlightedIndex].value);
                } else {
                    setIsOpen(!isOpen);
                }
                break;
            case 'ArrowDown':
                e.preventDefault();
                if (!isOpen) {
                    setIsOpen(true);
                } else {
                    setHighlightedIndex(prev =>
                        prev < filteredOptions.length - 1 ? prev + 1 : 0
                    );
                }
                break;
            case 'ArrowUp':
                e.preventDefault();
                if (!isOpen) {
                    setIsOpen(true);
                } else {
                    setHighlightedIndex(prev =>
                        prev > 0 ? prev - 1 : filteredOptions.length - 1
                    );
                }
                break;
            case 'Escape':
                setIsOpen(false);
                setHighlightedIndex(-1);
                break;
        }
    };

    const handleOptionClick = (optionValue: string) => {
        if (isMulti) {
            const currentValues = Array.isArray(value) ? [...value] : [];
            if (currentValues.includes(optionValue)) {
                // Remove
                onChange(currentValues.filter(v => v !== optionValue));
            } else {
                // Add
                if (currentValues.length < maxItems) {
                    onChange([...currentValues, optionValue]);
                }
            }
            // Keep open for multi select
        } else {
            onChange(optionValue);
            setIsOpen(false);
            setHighlightedIndex(-1);
        }
    };

    const handleRemoveChip = (e: React.MouseEvent, valToRemove: string) => {
        e.stopPropagation();
        if (Array.isArray(value)) {
            onChange(value.filter(v => v !== valToRemove));
        }
    };

    const handleToggle = () => {
        if (!disabled) {
            setIsOpen(!isOpen);
            setHighlightedIndex(-1);
        }
    };

    // Prepare chips for multi select
    const selectedOptions = isMulti && Array.isArray(value)
        ? options.filter(opt => value.includes(opt.value))
        : [];

    return (
        <div
            ref={selectRef}
            className={`custom-select-container ${className} ${error ? 'error' : ''} ${disabled ? 'disabled' : ''}`}
        >
            <div
                className={`custom-select-trigger ${isOpen ? 'open' : ''} ${error ? 'error' : ''}`}
                onClick={handleToggle}
                onKeyDown={handleKeyDown}
                tabIndex={disabled ? -1 : 0}
                role="combobox"
                aria-expanded={isOpen}
                aria-haspopup="listbox"
                aria-controls="custom-select-options"
                aria-label={placeholder}
            >
                {isMulti && selectedOptions.length > 0 ? (
                    <div className="custom-select-chips">
                        {/* Show first selected item */}
                        {selectedOptions.slice(0, 1).map(opt => (
                            <span key={opt.value} className="custom-select-chip">
                                {opt.label}
                                <span
                                    className="custom-select-chip-remove"
                                    onClick={(e) => handleRemoveChip(e, opt.value)}
                                >
                                    <svg width="10" height="10" viewBox="0 0 10 10" fill="currentColor">
                                        <path d="M1 1L9 9M9 1L1 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                                    </svg>
                                </span>
                            </span>
                        ))}
                        {/* Show count of remaining items */}
                        {selectedOptions.length > 1 && (
                            <span className="custom-select-chip">
                                +{selectedOptions.length - 1}
                            </span>
                        )}
                    </div>
                ) : (
                    <>
                        {showFlags && compact && !isMulti && value && (() => {
                            const selectedOpt = options.find(opt => opt.value === value);
                            return selectedOpt?.flagUrl && (
                                <Image
                                    width={20}
                                    height={20}
                                    src={selectedOpt.flagUrl}
                                    alt=""
                                    style={{
                                        width: '20px',
                                        height: '15px',
                                        objectFit: 'cover',
                                        borderRadius: '2px',
                                        marginRight: '8px',
                                        flexShrink: 0
                                    }}
                                />
                            );
                        })()}
                        <span className={`custom-select-value ${!value || (isMulti && value.length === 0) ? 'placeholder' : ''}`}>
                            {getSelectedLabel()}
                        </span>
                    </>
                )}

                <div className="custom-select-arrow" style={{ minWidth: "12px" }}>
                    <svg width="12" height="8" viewBox="0 0 12 8" fill="none">
                        <path
                            d="M1 1.5L6 6.5L11 1.5"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </div>
            </div>

            {isOpen && (
                <div
                    className="custom-select-dropdown"
                    style={compact && dropdownWidth ? { width: `${dropdownWidth}px` } : undefined}
                >
                    <div className="custom-select-search-container">
                        <div className="custom-select-search-icon">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M11 19C15.4183 19 19 15.4183 19 11C19 6.58172 15.4183 3 11 3C6.58172 3 3 6.58172 3 11C3 15.4183 6.58172 19 11 19Z" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M21 21L16.65 16.65" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </div>
                        <input
                            ref={searchInputRef}
                            type="text"
                            className="custom-select-search-input"
                            placeholder="Search..."
                            value={searchTerm}
                            onChange={(e) => {
                                setSearchTerm(e.target.value);
                                setHighlightedIndex(0);
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && filteredOptions.length > 0) {
                                    handleOptionClick(filteredOptions[highlightedIndex >= 0 ? highlightedIndex : 0].value);
                                }
                            }}
                        />
                    </div>
                    <div
                        ref={optionsRef}
                        id="custom-select-options"
                        className="custom-select-options"
                        role="listbox"
                    >
                        {filteredOptions.length > 0 ? (
                            filteredOptions.map((option, index) => {
                                const isSelected = isOptionSelected(option.value);
                                return (
                                    <div
                                        key={option.value}
                                        ref={el => { optionRefs.current[index] = el; }}
                                        className={`custom-select-option ${isSelected ? 'selected' : ''
                                            } ${highlightedIndex === index ? 'highlighted' : ''
                                            }`}
                                        onClick={() => handleOptionClick(option.value)}
                                        role="option"
                                        aria-selected={isSelected}
                                    >
                                        {isMulti && (
                                            <span style={{ marginRight: '8px', color: isSelected ? '#E25926' : '#D1D5DB' }}>
                                                {isSelected ? '☑' : '☐'}
                                            </span>
                                        )}
                                        {showFlags && option.flagUrl && (
                                            <Image
                                                src={option.flagUrl}
                                                alt=""
                                                width={20}
                                                height={15}
                                                style={{
                                                    width: '20px',
                                                    height: '15px',
                                                    objectFit: 'cover',
                                                    borderRadius: '2px',
                                                    marginRight: '10px',
                                                    flexShrink: 0
                                                }}
                                            />
                                        )}
                                        {option.fullLabel || option.label}
                                    </div>
                                );
                            })
                        ) : (
                            <div className="custom-select-no-results">
                                No results found
                            </div>
                        )}
                    </div>
                </div>
            )}

            {error && (
                <div className="custom-select-error">
                    {error}
                </div>
            )}
        </div>
    );
};

export default CustomSelect;
